import React, { useEffect } from 'react';
import {  Grid, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import stepIcon from '../../../assets/registration/terms_conditions.png'
import DocumentElement from '../components/DocumentElement';
import DocumentModal from '../modals/DocumentModal';
import {makeStyles} from '@mui/styles'
import ContainedButton from '../../../components/actions/ContainedButton';
import DisplayError from '../../../components/alerts/DisplayError';

/**
 * @module Layouts/Registration/views/TermsConditionForm
 * @description This is a functional component in JavaScript React that renders a form for accepting terms and
conditions. It receives several props (`form`, `actions`, `system`, `data`, and `modals`) and uses
them to display the necessary information and handle user interactions. It also uses the `useStyles`
hook from the `@mui/styles` library to define the component's styles. The `useEffect` hook is used
to call the `onRequestDocuments` function from the `actions` object when the component mounts for
the first time. The component also checks if all the necessary terms and conditions have been
accepted before enabling the "Continue" button.
 */

/**
 * 
 * @typedef TermsConditionForm
 * @property {MUIStyles} classes - The styles for the component 
 * @property {Boolean} loading - Indicates if initialization process is taking place
 * @property {Boolean} sending - Indicates if a process has started
 * @property {Object | String | Boolean} error - Variable that handles error, it can be a boolean, a string or an object 

 * @property {Array<Object>} agreements - List of agreement's documents 
 * @property {Function} onUpdateAgreement - Function to update information about a selected agreement document
 * @property {Function} onValidateDocuments - Checks if everything is ok with the agreement collection and allows to continue with 
 * the Enrollment Process

 */


const TermsConditionForm = ({form, actions, system, data, modals}) => {

    const classes = useStyles()

    /* `useEffect(() => { actions.onRequestDocuments() }, [])` is a React hook that is used to perform
    side effects in functional components. In this case, it is used to call the `onRequestDocuments`
    function from the `actions` object when the component mounts for the first time (indicated by
    the empty dependency array `[]`). This is similar to the `componentDidMount` lifecycle method in
    class components. */
    useEffect(() => {
        actions.onRequestDocuments()
    }, [])


    let isValid = true

    data.agreementCollections.forEach(item => {
        if(!item.accepted) isValid = false
    })

    let fileUrl = null

    if(data.agreementCollections.length){
        fileUrl = data.agreementCollections[0].url
    }

    return ( 
        <div className={classes.root}>
            <DocumentModal open={modals.document} 
                onClose={() => actions.onUpdateModalStatus('document', false)}
                file={data.document}/>
            <Grid container spacing={3} >
                <Grid item xs={12}>
                    <div className={classes.image_container}>
                        <img src={stepIcon} alt='' width='100%'/>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='h6' align='center' className={classes.title}>
                        Terms and Conditions
                    </Typography>
                    <Typography variant='body2' align='justify' className={classes.text}>
                        Prior to checking the box(es), you will need to view each document by clicking on the arrow. By checking the box(es) below, you are 
                        confirming that you have read and agree to each of the applicable policies and terms and conditions. Click on the arrow(s) below to view full 
                        agreement(s).
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={1}>
                        {data.agreementCollections.map((item, key)=>{
                            return(
                                <Grid item xs={12} key={`doc-${key}`}> 
                                    <DocumentElement document={item}
                                        onChange={(doc, type) => actions.onUpdateAgreement(doc, key, type)}
                                    />
                                </Grid>
                            )
                        })}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <DisplayError>{system.error}</DisplayError>
                    <ContainedButton loading={system.sending} fullWidth color='primaryDark' disabled={!isValid}
                        onClick={actions.onValidateDocuments}
                    >Continue</ContainedButton>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant='caption' className={classes.text}>{data?.issuer_statement}</Typography>
                </Grid>
            </Grid>
        </div>
     );
}

const useStyles = makeStyles(theme => ({
    root:{
        width:'100%',
        maxWidth:500,
        margin:'auto',
        padding:'40px 0px'
    },
    image_container:{
        width:200,
        margin:'auto',
        [theme.breakpoints.down('sm')]:{
            width:150,
        }
    },
    title:{
        fontWeight:600,
        lineHeight:1.5,
    },
    text:{
        fontWeight:500,
        color:blueGrey[400],
        [theme.breakpoints.down('sm')]:{
            fontSize:'0.875rem'
        }
    }
}))
 
export default TermsConditionForm;